import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { ApiError, explain } from '@/shared/api/problem'
import type { CoalitionDto } from '@/shared/api/types'
import { CODE_LIST_UNAVAILABLE, usePoliticsCodes } from '@/features/polling/useVocabulary'
import { createCoalition, updateCoalition } from './api'
import { Swatch, coalitionStatusLabel } from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Form a coalition, or record what happened to one.
 *
 * ## Dissolving is an edit, not a delete
 *
 * There is no DELETE route for a coalition and there should not be: the record of
 * which parties were in it has to survive the alliance. Setting the status to
 * DISSOLVED with the date is the whole of "it ended", and every membership row
 * keeps pointing at it.
 *
 * Parties are not chosen here. A membership is a fact about a PARTY with a join
 * date and a role, so it is recorded from the party's own panel — a multi-select
 * of parties on this form would have to invent one join date for all of them.
 */
export function CoalitionFormDialog({ open, coalition, onClose, onSaved }: {
  open: boolean
  /** `null` creates. */
  coalition: CoalitionDto | null
  onClose: () => void
  onSaved: (saved: CoalitionDto) => void
}) {
  const editing = coalition !== null

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [shortName, setShortName] = useState('')
  const [colourHex, setColourHex] = useState('')
  const statuses = usePoliticsCodes('coalitionStatus')
  const [statusCode, setStatusCode] = useState<string>('ACTIVE')
  const [formedOn, setFormedOn] = useState('')
  const [dissolvedOn, setDissolvedOn] = useState('')
  const [notes, setNotes] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    setCode(coalition?.code ?? '')
    setName(coalition?.name ?? '')
    setShortName(coalition?.shortName ?? '')
    setColourHex(coalition?.colourHex ?? '')
    setStatusCode(coalition?.statusCode ?? 'ACTIVE')
    setFormedOn(coalition?.formedOn ?? '')
    setDissolvedOn(coalition?.dissolvedOn ?? '')
    setNotes(coalition?.notes ?? '')
    setError(null)
    setFieldErrors({})
  }, [open, coalition])

  async function submit() {
    setBusy(true)
    setError(null)
    setFieldErrors({})
    const body = {
      code: code.trim(),
      name: name.trim(),
      shortName: shortName.trim() || null,
      colourHex: colourHex.trim() || null,
      statusCode,
      formedOn: formedOn || null,
      dissolvedOn: dissolvedOn || null,
      notes: notes.trim() || null,
    }
    try {
      const saved = editing
        ? await updateCoalition(coalition.id, body)
        : await createCoalition(body)
      onSaved(saved)
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors())
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const ready = code.trim().length > 0 && name.trim().length > 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${coalition.code}` : 'New coalition'}
      description={editing
        ? 'A dissolved coalition keeps its membership record — that is what it is for.'
        : 'Parties join afterwards, each with its own date and role.'}
      width="md"
      footer={(
        <>
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={!ready || busy} onClick={submit}>
            {busy ? 'Saving…' : editing ? 'Save' : 'Create'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-4">
            <Field label="Code" required error={fieldErrors.code} hint="Uppercased by the server.">
              <input className="erp-input w-full font-mono uppercase" value={code} maxLength={32}
                onChange={(e) => setCode(e.target.value)} placeholder="AZIMIO" />
            </Field>
          </div>
          <div className="col-span-8">
            <Field label="Name" required error={fieldErrors.name}>
              <input className="erp-input w-full" value={name} maxLength={200}
                onChange={(e) => setName(e.target.value)}
                placeholder="Azimio la Umoja One Kenya Coalition" />
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-6">
            <Field label="Short name" error={fieldErrors.shortName}>
              <input className="erp-input w-full" value={shortName} maxLength={32}
                onChange={(e) => setShortName(e.target.value)} placeholder="Azimio" />
            </Field>
          </div>
          <div className="col-span-6">
            <Field label="Status"
              error={fieldErrors.statusCode
                ?? (statuses.unavailable ? CODE_LIST_UNAVAILABLE : undefined)}>
              <select className="erp-input w-full" value={statusCode}
                disabled={statuses.loading || statuses.unavailable}
                onChange={(e) => setStatusCode(e.target.value)}>
                {statuses.loading && <option value={statusCode}>Loading…</option>}
                {statuses.codes.map((s) => (
                  <option key={s} value={s}>{coalitionStatusLabel(s)}</option>
                ))}
                {statusCode !== '' && !statuses.codes.includes(statusCode) && (
                  <option value={statusCode}>{coalitionStatusLabel(statusCode)}</option>
                )}
              </select>
            </Field>
          </div>
        </div>

        <Field label="Colour" error={fieldErrors.colourHex}
          hint="Six hex digits after a hash. Coalition chips and every chart that groups by alliance read this.">
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label="Pick the coalition colour"
              className="h-9 w-12 rounded-md border border-input bg-background p-1"
              value={/^#[0-9a-fA-F]{6}$/.test(colourHex) ? colourHex : '#888888'}
              onChange={(e) => setColourHex(e.target.value)}
            />
            <input className="erp-input flex-1 font-mono" value={colourHex} maxLength={7}
              onChange={(e) => setColourHex(e.target.value)} placeholder="#1d4ed8" />
            <Swatch colourHex={colourHex} className="h-5 w-5" />
            {colourHex && (
              <button type="button" className="erp-btn-ghost h-9 text-erp-xs"
                onClick={() => setColourHex('')}>
                No colour
              </button>
            )}
          </div>
        </Field>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-6">
            <Field label="Formed on" error={fieldErrors.formedOn}>
              <DatePicker value={formedOn} onChange={setFormedOn} className="w-full" />
            </Field>
          </div>
          <div className="col-span-6">
            <Field label="Dissolved on" error={fieldErrors.dissolvedOn}
              hint="Setting this does not remove anything — the memberships stay.">
              <DatePicker value={dissolvedOn} onChange={setDissolvedOn} className="w-full" />
            </Field>
          </div>
        </div>

        <Field label="Notes" error={fieldErrors.notes}>
          <textarea className="erp-input w-full min-h-[72px] py-2" value={notes} maxLength={4000}
            onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {statusCode === 'DISSOLVED' && !dissolvedOn && (
          <Note tone="bad">
            A dissolved coalition with no date cannot be ordered against anything else that
            happened that cycle. Give it the date it ended.
          </Note>
        )}
      </div>
    </Modal>
  )
}
